import { Box, Typography } from "@mui/material";
import CustomDatePickerField from "../../common/DatePicker/DatePicker";
import { groupingStyles } from "../../ExpenseVoucher/ExpenseRequest/ExpenseRequest";
import {
  CommonButton,
  CustomRequiredInputLabel,
} from "../../common/StyledComponents/StyledFields";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useRef, useState } from "react";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import ErrorPopup from "../../common/ErrorPopup";
import toast from "react-hot-toast";
import {
  apiRequest,
  catchErrorMessageHandler,
  displayDateFormat,
  getNextMonthDate,
  getUserDetails,
  getUserName,
  sortDataByDateAndTime,
} from "../../../infrastructure/backendService";
import apiPath from "../../../config/apiPaths.json";
import styles from "../../../App.module.scss";
import { useReactToPrint } from "react-to-print";

const printStyles = `
 @page {
      size: tabloid;
      margin: 24px;
    }
 
    /* Set layout to Landscape */
    body {
      width: 100vw;
      position: absolute;
    }
@media print {
  .printable {
    display: block !important;
  }
}
`;

const MySchedule = () => {
  const componentRef = useRef(null);

  const [isBtnLoading, setIsButtonLoading] = useState(false);
  const [mySchedules, setMySchedules] = useState<any>(null);
  const [isAPICalled, setIsAPICalled] = useState(false);

  const initialValues = {
    startDate: displayDateFormat(new Date()),
    endDate: displayDateFormat(getNextMonthDate(new Date())),
  };

  const validationSchema = Yup.object().shape({
    startDate: Yup.string().required("From date is required"),
    endDate: Yup.string()
      .required("To date is required")
      .test(
        "is-greater",
        "To date should not be less than from date",
        function (value) {
          const { startDate } = this.parent;
          if (!value || !startDate) return true; // If either date is not set, skip this validation
          return new Date(value) >= new Date(startDate);
        }
      ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      setIsButtonLoading(true);
      getMySchedules(values);
    },
    enableReinitialize: true,
  });

  const tableColumns = [
    { id: "date", fieldName: "Date" },
    { id: "startTime", fieldName: "Start Time" },
    { id: "endTime", fieldName: "End Time" },
    { id: "description", fieldName: "Description" },
  ];

  const getMySchedules = async (values: any) => {
    await apiRequest({
      method: "GET",
      endpoint: apiPath.schedules,
      params: {
        scheduleType: "PRIEST",
        schedulesForDay: false,
        priestId: getUserName(),
        startDate: displayDateFormat(new Date(values?.startDate)),
        endDate: displayDateFormat(new Date(values?.endDate)),
      },
      includeToken: true,
    })
      .then((res: any) => {
        const sortedSchedules = sortDataByDateAndTime(res?.data?.[0]?.schedulesByDate ?? [])
        setMySchedules(sortedSchedules ?? []);
        setIsButtonLoading(false);
        setIsAPICalled(true);
      })
      .catch((err: any) => {
        setIsButtonLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: printStyles,
    documentTitle: `${getUserDetails()} schedules from ${displayDateFormat(
      new Date(formik?.values?.startDate)
    )} to ${displayDateFormat(new Date(formik?.values?.endDate))}`,
  });

  const handleChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  const renderFieldsComponent = () => {
    return (
      <Box className="common_box_shadow" sx={{ mt: "20px" }}>
        <Box sx={groupingStyles.groupedFieldsContainer}>
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("From Date", true)}
            <CustomDatePickerField
              date={formik.values.startDate}
              handleDateChange={handleChange}
              errorMessage={
                formik.touched.startDate ? formik.errors.startDate : ""
              }
              name="startDate"
            />
          </Box>
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("To Date", true)}
            <CustomDatePickerField
              date={formik.values.endDate}
              handleDateChange={handleChange}
              errorMessage={formik.touched.endDate ? formik.errors.endDate : ""}
              name="endDate"
            />
          </Box>
        </Box>
        <CommonButton
          onClick={() => {
            !isBtnLoading && formik.handleSubmit();
          }}
          sx={{
            mt: `${
              (formik.touched.startDate && formik.errors.startDate) ||
              (formik.touched.endDate && formik.errors.endDate)
                ? "20px"
                : "5px"
            }`,
          }}
        >
          <ButtonLoader isBtnLoading={isBtnLoading} btnText={"Submit"} />
        </CommonButton>
      </Box>
    );
  };

  const renderSCheduleData = () => {
    return (
      <Box sx={{ my: "20px" }}>
        {mySchedules?.length > 0 ? (
          <Box ref={componentRef}>
            <Box sx={{ textAlign: "center" }} className="print_display">
              <Typography className="main_heading">Priest Schedules</Typography>
              <Typography
                className="sub_heading"
                sx={{ mt: "15px", mb: "10px" }}
              >
                {getUserDetails()}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: "10px",
              }}
            >
              <Typography className="sub_heading">
                Schedules from{" "}
                {displayDateFormat(new Date(formik?.values?.startDate))} to{" "}
                {displayDateFormat(new Date(formik?.values?.endDate))}
              </Typography>
              <CommonButton onClick={handlePrint} className="no_print">
                Print
              </CommonButton>
            </Box>
            <table className={styles.table_common_container}>
              <thead>
                {/* <th style={{ width: "32px" }}>Sr No</th> */}
                {tableColumns?.map((each: any) => {
                  if (each?.id === "startTime" || each?.id === "endTime") {
                    return (
                      <th className={styles.no_wrap}>
                        {each?.fieldName}
                      </th>
                    );
                  } else {
                    return <th>{each?.fieldName}</th>;
                  }
                })}
              </thead>
              <tbody>
                {mySchedules?.map?.((row: any, index: number) => (
                  <tr key={index}>
                    {/* <td>{index + 1}</td> */}
                    {tableColumns?.map((each: any) => {
                      if (each?.id === "startTime" || each?.id === "endTime") {
                        return (
                          <td style={{ whiteSpace: "nowrap" }}>
                            {row[each?.id]}
                          </td>
                        );
                      } else {
                        return <td>{row[each?.id]}</td>;
                      }
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        ) : (
          <Typography
            className="sub_heading"
            sx={{ fontSize: "18px !important" }}
          >
            There are no schedules within the given date range.
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      <Typography className="sub_heading">My Schedule</Typography>
      {renderFieldsComponent()}
      {isAPICalled && renderSCheduleData()}
    </>
  );
};

export default MySchedule;
